import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { Lock, Shield, Mail, KeyRound, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("credentials"); // "credentials" | "otp"
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [code, setCode] = useState("");
  const [otpMessage, setOtpMessage] = useState("");

  const handleCredentials = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, formData);
      if (data.otp_required) {
        setOtpMessage(data.message || "A verification code has been sent to the admin email.");
        setStep("otp");
        toast.success("Verification code sent");
      }
    } catch (error) {
      const status = error.response?.status;
      if (status === 429) {
        toast.error("Too many attempts. Please wait a minute and try again.");
      } else {
        toast.error("Invalid admin credentials");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.trim().length < 6) {
      toast.error("Enter the 6-digit code");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login/verify`, {
        email: formData.email,
        code: code.trim(),
      });
      const { access_token } = data;
      localStorage.setItem("admin_token", access_token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
      toast.success("Welcome to Admin Dashboard");
      navigate("/admin/dashboard");
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Invalid verification code");
    } finally {
      setLoading(false);
    }
  };

  const backToCredentials = () => {
    setStep("credentials");
    setCode("");
    setFormData({ ...formData, password: "" });
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center py-12 px-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-orange-500 rounded-xl flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "Manrope, sans-serif" }}>
            Admin Login
          </h1>
          <p className="text-slate-400 mt-2">ProToCall Management Portal</p>
        </div>

        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="pt-6">
            {step === "credentials" ? (
              <form onSubmit={handleCredentials} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-slate-300">Admin Email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="pl-10 h-11 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                      data-testid="admin-email-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="password" className="text-slate-300">Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="••••••••"
                      className="pl-10 h-11 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                      data-testid="admin-password-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold h-11"
                  data-testid="admin-login-btn"
                >
                  {loading ? "Verifying..." : "Continue"}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleVerify} className="space-y-4" data-testid="admin-otp-form">
                <div className="text-center mb-2">
                  <div className="w-12 h-12 bg-slate-700 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <KeyRound className="w-6 h-6 text-orange-400" />
                  </div>
                  <p className="text-slate-300 text-sm" data-testid="admin-otp-message">{otpMessage}</p>
                </div>

                <div>
                  <Label htmlFor="code" className="text-slate-300">Verification Code</Label>
                  <Input
                    id="code"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="______"
                    className="mt-1 h-14 text-center text-2xl tracking-[0.5em] bg-slate-700 border-slate-600 text-white placeholder:text-slate-600"
                    data-testid="admin-otp-input"
                    autoFocus
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold h-11"
                  data-testid="admin-otp-verify-btn"
                >
                  {loading ? "Signing in..." : "Verify & Sign In"}
                </Button>

                <button
                  type="button"
                  onClick={backToCredentials}
                  className="w-full flex items-center justify-center gap-1.5 text-sm text-slate-400 hover:text-orange-400 transition-colors"
                  data-testid="admin-otp-back-btn"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> Back
                </button>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-slate-500 text-sm mt-6">
          <a href="/" className="hover:text-orange-400">← Back to website</a>
        </p>
      </div>
    </div>
  );
}
